import type { ManagerApi } from '@/Api'
import { ensureError } from '@iot-middleware/shared'
import { z } from 'zod'
import { type Store } from '@/store'

const AuthUser = z.object({
  _id: z.string(),
  organisationId: z.string().nullable(),
  secondFactor: z.boolean().optional(),
  role: z.string(),
})
const AuthResponse = z.object({
  user: AuthUser,
  accessToken: z.string(),
})
const AuthError = z.object({
  message: z.string(),
  errors: z.array(z.string()).optional(),
})
export type AuthUser = z.infer<typeof AuthUser>
export type AuthResponse = z.infer<typeof AuthResponse>
export type AuthError = z.infer<typeof AuthError>

export type LoginRequest =
  | {
      strategy: 'local'
      email: string
      password: string
      faToken?: string
    }
  | {
      strategy: 'datasphere-passthrough'
      instance: string
      email: string
      password: string
    }

export type LoginResult =
  | {
      case: 'success'
      response: AuthResponse
    }
  | {
      case: 'error'
      error: AuthError
    }

export async function login(
  api: ManagerApi,
  request: LoginRequest,
): Promise<LoginResult> {
  try {
    const r = await api.authenticate(request)
    const response = AuthResponse.safeParse(r)
    if (!response.success) {
      return {
        case: 'error',
        error: r.error,
      }
    }
    return {
      case: 'success',
      response: response.data,
    }
  } catch (e) {
    const error = AuthError.safeParse(e)
    if (error.success) {
      return {
        case: 'error',
        error: error.data,
      }
    } else {
      throw ensureError(e)
    }
  }
}

export async function setupSecondFactor(
  api: ManagerApi,
  secret: string,
  confirmToken: string,
): Promise<void> {
  await api.service('second-factor').create({
    faToken: secret,
    confirmFactor: confirmToken,
  })
}

export async function findFirstOrganisationId(
  api: ManagerApi,
): Promise<string | null> {
  const organisations = await api
    .service('organisations')
    .find({ query: { $limit: 1 } })
  return organisations.data[0]?._id ?? null
}

export async function setOrganisationId(
  api: ManagerApi,
  store: Store,
  user: AuthUser,
): Promise<void> {
  if (user.organisationId) {
    store.commit('setOrganisationId', user.organisationId)
  } else {
    const organisationId = await findFirstOrganisationId(api)
    if (organisationId) {
      store.commit('setOrganisationId', organisationId)
    }
  }
}
